import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, tap, timeout } from 'rxjs';
import { environment } from '@env/environment';
import { ROLES, Role, UserProfile, UserProfileResponse } from '@core/models/role.model';

export type ProfileStatus = 'idle' | 'loading' | 'loaded' | 'not-found' | 'error';
export const USER_PROFILE_LOAD_TIMEOUT_MS = 10000;

@Injectable({ providedIn: 'root' })
export class UserProfileService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.apiUrl}/api/users/me`;

  private readonly _profile = signal<UserProfile | null>(null);
  private readonly _status = signal<ProfileStatus>('idle');

  readonly profile = this._profile.asReadonly();
  readonly status = this._status.asReadonly();
  readonly role = computed<Role | null>(() => this._profile()?.role ?? null);
  readonly ready = computed(() => this._status() !== 'idle' && this._status() !== 'loading');

  loadProfile(): Observable<UserProfile | null> {
    this._status.set('loading');
    this._profile.set(null);

    if (!environment.production && environment.mockRole) {
      const fakeProfile: UserProfile = {
        id: 'mock-local-user',
        rut: '11.111.111-1',
        nombreCompleto: 'Dev Local',
        email: 'dev@local.test',
        telefono: '900000000',
        codigoPais: '+56',
        region: 'Metropolitana de Santiago',
        direccion: 'Av. Dev',
        numeroDireccion: '1',
        comuna: 'Santiago',
        referenciaDireccion: null,
        createdAt: new Date().toISOString(),
        role: this.normalizeRole(environment.mockRole),
      };
      this._profile.set(fakeProfile);
      this._status.set('loaded');
      return of(fakeProfile);
    }

    return this.http.get<UserProfileResponse>(this.endpoint).pipe(
      // Prevent startup/guard deadlocks if the backend never responds.
      timeout({ first: USER_PROFILE_LOAD_TIMEOUT_MS }),
      map((response) => this.toProfile(response)),
      tap((profile) => {
        this._profile.set(profile);
        this._status.set('loaded');
      }),
      catchError((error: unknown) => {
        this._profile.set(null);
        this._status.set(this.isNotFoundError(error) ? 'not-found' : 'error');
        return of(null);
      })
    );
  }

  reset(): void {
    this._profile.set(null);
    this._status.set('idle');
  }

  private toProfile(response: UserProfileResponse): UserProfile {
    return {
      id: response.id,
      rut: response.rut,
      nombreCompleto: response.nombreCompleto ?? response.nombre ?? '',
      email: response.email,
      telefono: response.telefono,
      codigoPais: response.codigoPais,
      region: response.region,
      direccion: response.direccion,
      numeroDireccion: response.numeroDireccion,
      comuna: response.comuna,
      referenciaDireccion: response.referenciaDireccion ?? null,
      createdAt: response.createdAt,
      role: this.normalizeRole(response.role),
    };
  }

  private normalizeRole(raw: string | null | undefined): Role | null {
    if (!raw) return null;
    const upper = raw.toUpperCase();
    return (Object.values(ROLES) as string[]).includes(upper) ? (upper as Role) : null;
  }

  private isNotFoundError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 404;
  }
}
